import React, { useEffect, useMemo } from 'react';
import { motion } from "framer-motion";
import '../css/style.css';
import { BsTelephone, BsGeoAlt, BsBuilding } from 'react-icons/bs';
import { FaUser, FaRegIdCard } from "react-icons/fa";
import { MdEmail } from 'react-icons/md';
import Loader from "./kontakt-loader";
import { getLocalizedText } from '../utils/textLocalization';
import {
  EditableContent,
  useEditry,
  usePreloadPublicData,
  usePublicContentList,
} from '@lazylabs-dev/editry-react';
import { isEditryConfigured } from '../utils/editryConfig';
import { getCurrentLanguage } from '../utils/languageSwitcher';

const LOCALE_TO_CONTENT_LANGUAGE = {
  'cs-CZ': 'cs',
  'sk-SK': 'sk',
  'pl-PL': 'pl',
  'hu-HU': 'hu',
};

const getPreferredLanguages = (locale) => {
  const preferred = LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';
  return preferred === 'cs' ? ['cs', 'default'] : [preferred, 'cs', 'default'];
};

const getEditryLanguage = (locale) => LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';

const normalizeContentLanguage = (language) => {
  if (typeof language !== 'string') {
    return 'default';
  }

  const normalized = language.trim().toLowerCase();
  return normalized || 'default';
};

const findLocalizedRecord = (contentList, contentKey, preferredLanguages = ['cs']) => {
  if (!Array.isArray(contentList)) {
    return null;
  }

  const records = contentList.filter((item) => item.content_key === contentKey);
  if (!records.length) {
    return null;
  }

  for (const language of preferredLanguages) {
    const targetLanguage = normalizeContentLanguage(language);
    const record = records.find((item) => normalizeContentLanguage(item.language) === targetLanguage);
    if (record) {
      return record;
    }
  }

  return records[0] || null;
};

const getLocalizedTextValue = (contentList, contentKey, fallback = '', preferredLanguages = ['cs']) => {
  const record = findLocalizedRecord(contentList, contentKey, preferredLanguages);
  const value = record?.value;

  if (typeof value === 'string' && value.trim() !== '') {
    return value;
  }

  return fallback;
};

const getLocalizedListValue = (contentList, contentKey, preferredLanguages = ['cs']) => {
  const record = findLocalizedRecord(contentList, contentKey, preferredLanguages);
  const value = record?.value;

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

function Kontakt() {
  const locale = getCurrentLanguage();
  const preferredLanguages = useMemo(() => getPreferredLanguages(locale), [locale]);
  const editryLanguage = useMemo(() => getEditryLanguage(locale), [locale]);
  const editryReady = isEditryConfigured();
  const { overlayEnabled, adminSession } = useEditry();
  const isEditModeActive = overlayEnabled && Boolean(adminSession?.token);

  usePreloadPublicData({ auto: editryReady });
  const contentListResponse = usePublicContentList();

  const allContent = useMemo(
    () => contentListResponse.data?.data || [],
    [contentListResponse.data]
  );

  const data = useMemo(() => ({
    rychlyKontakt: {
      telefon: getLocalizedTextValue(allContent, 'kontakt_kontakt_rychlykontakt_telefon', '', preferredLanguages),
      email: getLocalizedTextValue(allContent, 'kontakt_kontakt_rychlykontakt_email', '', preferredLanguages),
    },
    prvniRadek: {
      nazev: getLocalizedTextValue(allContent, 'kontakt_kontakt_prvniradek_nazev', '', preferredLanguages),
      jmenoFirmy: getLocalizedTextValue(allContent, 'kontakt_kontakt_prvniradek_jmenofirmy', '', preferredLanguages),
      adresa: getLocalizedTextValue(allContent, 'kontakt_kontakt_prvniradek_adresa', '', preferredLanguages),
      mesto: getLocalizedTextValue(allContent, 'kontakt_kontakt_prvniradek_mesto', '', preferredLanguages),
      psc: getLocalizedTextValue(allContent, 'kontakt_kontakt_prvniradek_psc', '', preferredLanguages),
      zastupci: getLocalizedListValue(allContent, 'kontakt_kontakt_prvniradek_zastupci', preferredLanguages),
    },
    druhyRadek: {
      reklamace: {
        nazev: getLocalizedTextValue(allContent, 'kontakt_kontakt_druhyradek_reklamace_nazev', '', preferredLanguages),
        jmenoFirmy: getLocalizedTextValue(allContent, 'kontakt_kontakt_druhyradek_reklamace_jmenofirmy', '', preferredLanguages),
        adresa: getLocalizedTextValue(allContent, 'kontakt_kontakt_druhyradek_reklamace_adresa', '', preferredLanguages),
        mesto: getLocalizedTextValue(allContent, 'kontakt_kontakt_druhyradek_reklamace_mesto', '', preferredLanguages),
        psc: getLocalizedTextValue(allContent, 'kontakt_kontakt_druhyradek_reklamace_psc', '', preferredLanguages),
        telefon: getLocalizedTextValue(allContent, 'kontakt_kontakt_druhyradek_reklamace_telefon', '', preferredLanguages),
        email: getLocalizedTextValue(allContent, 'kontakt_kontakt_druhyradek_reklamace_email', '', preferredLanguages),
        text: getLocalizedTextValue(allContent, 'kontakt_kontakt_druhyradek_reklamace_text', '', preferredLanguages),
      },
      malyBox: {
        nazev: getLocalizedTextValue(allContent, 'kontakt_kontakt_druhyradek_malybox_nazev', '', preferredLanguages),
        pozice: getLocalizedTextValue(allContent, 'kontakt_kontakt_druhyradek_malybox_pozice', '', preferredLanguages),
        jmeno: getLocalizedTextValue(allContent, 'kontakt_kontakt_druhyradek_malybox_jmeno', '', preferredLanguages),
        telefon: getLocalizedTextValue(allContent, 'kontakt_kontakt_druhyradek_malybox_telefon', '', preferredLanguages),
        email: getLocalizedTextValue(allContent, 'kontakt_kontakt_druhyradek_malybox_email', '', preferredLanguages),
      },
    },
    fakturacni: {
      nazev: getLocalizedTextValue(allContent, 'kontakt_kontakt_fakturacni_nazev', '', preferredLanguages),
      adresa: getLocalizedTextValue(allContent, 'kontakt_kontakt_fakturacni_adresa', '', preferredLanguages),
      mesto: getLocalizedTextValue(allContent, 'kontakt_kontakt_fakturacni_mesto', '', preferredLanguages),
      psc: getLocalizedTextValue(allContent, 'kontakt_kontakt_fakturacni_psc', '', preferredLanguages),
      ico: getLocalizedTextValue(allContent, 'kontakt_kontakt_fakturacni_ico', '', preferredLanguages),
      dic: getLocalizedTextValue(allContent, 'kontakt_kontakt_fakturacni_dic', '', preferredLanguages),
      or: getLocalizedTextValue(allContent, 'kontakt_kontakt_fakturacni_or', '', preferredLanguages),
      spisovaZnacka: getLocalizedTextValue(allContent, 'kontakt_kontakt_fakturacni_spisovaznacka', '', preferredLanguages),
    },
  }), [allContent, preferredLanguages]);

  const list = data.prvniRadek.zastupci;
  const loading = editryReady && contentListResponse.loading && !allContent.length;
  const zastupciFallbackJson = useMemo(() => JSON.stringify(list, null, 2), [list]);

  const renderEditableText = (contentKey, fallback = '') => {
    if (!isEditModeActive) {
      return fallback;
    }

    return (
      <EditableContent
        contentKey={contentKey}
        language={editryLanguage}
        as="span"
        fallback={fallback}
        showFallbackWhileLoading={false}
      />
    );
  };

  // Get localized text
  const quickContactTitleText = getLocalizedText('contact.quick_contact_title');
  const quickContactText = getLocalizedText('contact.quick_contact');
  const callUsText = getLocalizedText('contact.call_us');
  const telText = getLocalizedText('contact.tel');
  const billingTitleText = getLocalizedText('contact.billing_title');
  const billingInfoText = getLocalizedText('contact.billing_info');
  const vatIdText = getLocalizedText('contact.vat_id');
  const registryText = getLocalizedText('contact.registry');
  const fileNumberText = getLocalizedText('contact.file_number');

  useEffect(() => {
    document.title = getLocalizedText('contact.page_title');
  }, []);

  if (loading) {
    return (
      <>
        <Loader />
      </>
    )
  }

  return (
    <motion.div
      key="kontakt"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="kontakt-container"
    >
      <div className="top-contact">
        <div className="contact-title">{quickContactTitleText}</div>
        <div className="contact-phone">
          <div className="contact-label">{callUsText}</div>
          <div className="contact-item">
            <span className="contact-value tel">
              {telText} {renderEditableText('kontakt_kontakt_rychlykontakt_telefon', data.rychlyKontakt.telefon)}
            </span>
          </div>
        </div>
        <div className="contact-email">
          <div className="contact-label">{quickContactText}</div>
          <div className="contact-item">
            <a href={data.rychlyKontakt.email ? `mailto:${data.rychlyKontakt.email}` : undefined} className="contact-value email">
              {isEditModeActive ? (
                <EditableContent
                  contentKey="kontakt_kontakt_rychlykontakt_email"
                  language={editryLanguage}
                  as="span"
                  fallback={data.rychlyKontakt.email.toUpperCase()}
                  showFallbackWhileLoading={false}
                />
              ) : (
                data.rychlyKontakt.email.toUpperCase()
              )}
            </a>
          </div>
        </div>
      </div>
      
      <div className="contact-content">
        {/* E-shop zabírá plnou šířku nahoře */}
        <div className="contact-card e-shop-card">
          <h2 className="card-title">{renderEditableText('kontakt_kontakt_prvniradek_nazev', data.prvniRadek.nazev)}</h2>
          <div className="card-content">
            <div className="address-block">
              <p className="info-content"><BsBuilding className="info-icon" /> {renderEditableText('kontakt_kontakt_prvniradek_jmenofirmy', data.prvniRadek.jmenoFirmy)}</p>
              <p className="info-content"><BsGeoAlt className="info-icon" /> {renderEditableText('kontakt_kontakt_prvniradek_adresa', data.prvniRadek.adresa)}</p>
              <p className="info-content" style={{marginLeft: "30px"}}>
                {renderEditableText('kontakt_kontakt_prvniradek_mesto', data.prvniRadek.mesto)}, {renderEditableText('kontakt_kontakt_prvniradek_psc', data.prvniRadek.psc)}
              </p>
            </div>
            
            <div className="contact-regions">
            {isEditModeActive && (
              <div className="contact-region" style={{ width: '100%' }}>
                <h3 className="info-title">Obchodni zastoupeni (JSON):</h3>
                <EditableContent
                  contentKey="kontakt_kontakt_prvniradek_zastupci"
                  language={editryLanguage}
                  as="pre"
                  className="info-content"
                  contentType="list"
                  fallback={zastupciFallbackJson}
                  showFallbackWhileLoading={false}
                />
              </div>
            )}
            {list.map(element => (
              <div className="contact-region" key={element.nazev}>
                <h3 className="info-title">{element.nazev}:</h3>
                {element.jmeno && (
                  <p className="info-content"><FaUser className="info-icon" /> {element.jmeno}</p>
                )}
                <p className="info-content"><BsTelephone className="info-icon" /> {telText} {element.telefon}</p>
                <p className="info-content"><MdEmail className="info-icon" /> <a href={"mailto:" + element.email} className="email">{element.email}</a></p>
              </div>
            ))}
            </div>
          </div>
        </div>
        
        {/* Reklamace a International na jednom řádku */}
        <div className="contact-row">
          <div className="contact-card reklamace-card">
            <h2 className="card-title">{renderEditableText('kontakt_kontakt_druhyradek_reklamace_nazev', data.druhyRadek.reklamace.nazev)}</h2>
            <div className="card-content">
              <p className="info-content"><BsBuilding className="info-icon" /> {renderEditableText('kontakt_kontakt_druhyradek_reklamace_jmenofirmy', data.druhyRadek.reklamace.jmenoFirmy)}</p>
              <p className="info-content"><BsGeoAlt className="info-icon" /> {renderEditableText('kontakt_kontakt_druhyradek_reklamace_adresa', data.druhyRadek.reklamace.adresa)}</p>
              <p className="info-content" style={{marginLeft: "5px"}}>
                {renderEditableText('kontakt_kontakt_druhyradek_reklamace_mesto', data.druhyRadek.reklamace.mesto)}, {renderEditableText('kontakt_kontakt_druhyradek_reklamace_psc', data.druhyRadek.reklamace.psc)}
              </p>
              
              <p className="info-content info-spacing"><BsTelephone className="info-icon" /> {telText} {renderEditableText('kontakt_kontakt_druhyradek_reklamace_telefon', data.druhyRadek.reklamace.telefon)}</p>
              <p className="info-content">
                <MdEmail className="info-icon" />
                <a href={data.druhyRadek.reklamace.email ? `mailto:${data.druhyRadek.reklamace.email}` : undefined} className="email">
                  {isEditModeActive ? (
                    <EditableContent
                      contentKey="kontakt_kontakt_druhyradek_reklamace_email"
                      language={editryLanguage}
                      as="span"
                      fallback={data.druhyRadek.reklamace.email}
                      showFallbackWhileLoading={false}
                    />
                  ) : (
                    data.druhyRadek.reklamace.email
                  )}
                </a>
              </p>
              
              {isEditModeActive ? (
                <EditableContent
                  contentKey="kontakt_kontakt_druhyradek_reklamace_text"
                  language={editryLanguage}
                  as="p"
                  className="info-content info-note"
                  contentType="html"
                  fallback={data.druhyRadek.reklamace.text}
                  showFallbackWhileLoading={false}
                />
              ) : (
                <p className="info-content info-note" dangerouslySetInnerHTML={{ __html: data.druhyRadek.reklamace.text }} />
              )}
            </div>
          </div>
          
          <div className="contact-card international-card">
            <h2 className="card-title">{renderEditableText('kontakt_kontakt_druhyradek_malybox_nazev', data.druhyRadek.malyBox.nazev)}</h2>
            <div className="card-content">
              <div className="international-content international-content-data" style={{display: "block", position: "relative", top: "50px"}}>
                <p className="info-content inernational-content-email"><FaRegIdCard className="info-icon" /> {renderEditableText('kontakt_kontakt_druhyradek_malybox_pozice', data.druhyRadek.malyBox.pozice)}</p>
                <p className="info-content inernational-content-email"><FaUser className="info-icon" /> {renderEditableText('kontakt_kontakt_druhyradek_malybox_jmeno', data.druhyRadek.malyBox.jmeno)}</p>
                <p className="info-content inernational-content-email"><BsTelephone className="info-icon" /> {telText} {renderEditableText('kontakt_kontakt_druhyradek_malybox_telefon', data.druhyRadek.malyBox.telefon)}</p>
                <p className="info-content inernational-content-email">
                  <MdEmail className="info-icon" />
                  <a href={data.druhyRadek.malyBox.email ? `mailto:${data.druhyRadek.malyBox.email}` : undefined} className="email">
                    {isEditModeActive ? (
                      <EditableContent
                        contentKey="kontakt_kontakt_druhyradek_malybox_email"
                        language={editryLanguage}
                        as="span"
                        fallback={data.druhyRadek.malyBox.email}
                        showFallbackWhileLoading={false}
                      />
                    ) : (
                      data.druhyRadek.malyBox.email
                    )}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Fakturační údaje opět přes celou šířku dole */}
        <div className="contact-card fakturacni-card">
          <h2 className="card-title">{billingTitleText}</h2>
          <div className="card-content">
            <div className="fakturacni-columns">
              <div className="fakturacni-column">
                <p className="info-content"><BsBuilding className="info-icon" /> {renderEditableText('kontakt_kontakt_fakturacni_nazev', data.fakturacni.nazev)}</p>
                <p className="info-content"><BsGeoAlt className="info-icon" /> {renderEditableText('kontakt_kontakt_fakturacni_adresa', data.fakturacni.adresa)}</p>
                <p className="info-content" style={{marginLeft: "30px"}}>{renderEditableText('kontakt_kontakt_fakturacni_mesto', data.fakturacni.mesto)}, {renderEditableText('kontakt_kontakt_fakturacni_psc', data.fakturacni.psc)}</p>
              </div>
              
              <div className="fakturacni-column">
                <p className="info-content">{billingInfoText} {renderEditableText('kontakt_kontakt_fakturacni_ico', data.fakturacni.ico)}</p>
                <p className="info-content">{vatIdText} {renderEditableText('kontakt_kontakt_fakturacni_dic', data.fakturacni.dic)}</p>
                <p className="info-content">{registryText} {renderEditableText('kontakt_kontakt_fakturacni_or', data.fakturacni.or)}</p>
                <p className="info-content">{fileNumberText} {renderEditableText('kontakt_kontakt_fakturacni_spisovaznacka', data.fakturacni.spisovaZnacka)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default Kontakt;